// Turns an aim point into an actual landing point, using a difficulty tier's
// own throwProfile (see core/ai-difficulty.js) to decide how far off that
// landing can be. Pure and synchronous - no DOM, no game rules - so it's
// easy to test in isolation and easy to extend later without touching any
// of its callers: today throwProfile only has `accuracyStdDev` (v1 is
// "spread only," by design - see the AI implementation plan), but every
// field it might eventually gain - a systematic aim bias, a pressure
// multiplier for a clutch dart, decision imperfection - reads off this same
// object, so a caller building a throwProfile today never needs to change
// once those exist.
import { targetPoint, resolvePoint } from './dartboard-geometry.js';

// A standard normal (mean 0, stddev 1) sample via the Box-Muller transform -
// no library needed for a single Gaussian source, and every caller here
// just scales it by whatever stddev the situation calls for.
function gaussianRandom() {
    // Math.random() can return exactly 0; log(0) is -Infinity, so it's
    // nudged into (0, 1] instead of [0, 1).
    const u1 = 1 - Math.random();
    const u2 = Math.random();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

// One simulated dart: aim at `target` ({ number, ring } - see
// dartboard-geometry.js's targetPoint for the valid ring values), land
// somewhere scattered around it by `throwProfile.accuracyStdDev` (board
// viewBox units - the same scale as core/dartboard-geometry.js's RADII;
// smaller is more accurate), and resolve what that landing point actually
// scored. The noise is an isotropic 2D Gaussian (independent x/y, same
// stddev) - equally likely to miss in any direction, the right default
// until throwProfile has an actual bias to apply.
export function simulateThrow(target, throwProfile) {
    const aimPoint = targetPoint(target);
    const stdDev = throwProfile.accuracyStdDev;
    const actualPoint = {
        x: aimPoint.x + gaussianRandom() * stdDev,
        y: aimPoint.y + gaussianRandom() * stdDev
    };
    const result = resolvePoint(actualPoint);
    return { aim: target, aimPoint, actualPoint, result };
}
